import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { setMessage } from './message';

import conversionRateService from '../services/conversion-rate.service';


export const fetchConversionRateList = createAsyncThunk(
    'conversionRate/fetchConversionRateList',
    async ({ page, pagesize, search, groupId, ordering }, thunkAPI) => {
        try {
            const response = await conversionRateService.getConversionList(page, pagesize, search, groupId, ordering);
            return response.data;
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const fetchArchivedConversionRateList = createAsyncThunk(
    'conversionRate/fetchArchivedConversionRateList',
    async ({ page, pagesize, search, ordering }, thunkAPI) => {
        try {
            const response = await conversionRateService.getConversionRateArchiveList(page, pagesize, search, ordering);
            return response.data;
        } catch (error) {
            const message =
                (error.response &&
                    error.response.data &&
                    error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    },
);
export const fetchConversionRateById = createAsyncThunk(
    'conversionRate/fetchConversionRateById',
    async (id, { rejectWithValue }) => {
        try {
            const response = await conversionRateService.getConversionRateById(id);
            return response.data; // Return the profile data
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);
export const createConversionRate = createAsyncThunk(
    'conversionRate/createConversionRate',
    async ({ values }, thunkAPI) => {
        try {
            const response = await conversionRateService.addConversionRate(values);
            return response.data;
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);
export const updateConversion = createAsyncThunk(
    'conversionRate/updateConversion',
    async ({ values }, thunkAPI) => {
        try {
            const response = await conversionRateService.updateConversionRate(values);
            return response.data; // Return the updated die group data
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const deleteConversionRate = createAsyncThunk(
    'conversionRate/deleteConversionRate',
    async (id, thunkAPI) => {
        try {
            await conversionRateService.deleteConversionRate(id); // Ensure `id` is used here
            return id; // Return the ID of the deleted group
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);


export const unArchiveConversionRate = createAsyncThunk(
    'conversionRate/unArchiveConversionRate',
    async (id, thunkAPI) => {
        try {
            await conversionRateService.unArchiveConversionRate(id); // Ensure `id` is used here
            return id; // Return the ID of the deleted group
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

const initialState = {
    data: [], // List
    list: [],
    totalPages: 0,
    currentPage: 1,
    totalRecord: 0,
    status: 'idle',
    error: null,
    loading: false,
    hasMore: true,
};


const conversionRateSlice = createSlice({
    name: 'conversionRate',
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        resetConversionRateData: (state) => {
            state.data = []; // This will clear the list
        },
        resetConversionRateList: (state) => {
            state.list = []; // This will clear the list
        },
        resetCurrentConversionRatePage: (state) => {
            state.currentPage = 1;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchConversionRateList.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchConversionRateList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedPage = action.payload?.page || state.currentPage;
                const resultsData = action.payload?.results?.data || action.payload?.results || action.payload?.data || [];
                state.data = Array.isArray(resultsData) ? resultsData : [];  // Update to match your API response
                state.totalRecord = action.payload?.count || 0; // Handle count (total records)
                state.totalPages = action.payload?.totalPages || 0; // Handle totalPages
                state.currentPage = action.payload?.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...(Array.isArray(state.list) ? state.list : []), ...(Array.isArray(resultsData) ? resultsData : [])];
                state.hasMore = !!action.payload?.next;
            })
            .addCase(fetchConversionRateList.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
            });
        builder
            .addCase(fetchArchivedConversionRateList.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchArchivedConversionRateList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedPage = action.payload?.page || state.currentPage;
                const resultsData = action.payload?.results?.data || action.payload?.results || action.payload?.data || [];
                state.data = Array.isArray(resultsData) ? resultsData : [];  // Update to match your API response
                state.totalRecord = action.payload?.count || 0; // Handle count (total records)
                state.totalPages = action.payload?.totalPages || 0; // Handle totalPages
                state.currentPage = action.payload?.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...(Array.isArray(state.list) ? state.list : []), ...(Array.isArray(resultsData) ? resultsData : [])];
                state.hasMore = !!action.payload?.next;
            })
            .addCase(fetchArchivedConversionRateList.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
            });
        builder
            .addCase(fetchConversionRateById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchConversionRateById.fulfilled, (state, action) => {
                state.loading = false;
                // Store the payload as-is to match component expectations (response.results.data)
                state.activeProfileRecord = action.payload || null;
            })
            .addCase(fetchConversionRateById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
        builder
            .addCase(createConversionRate.pending, state => {
                state.status = 'loading';
            })
            .addCase(createConversionRate.fulfilled, (state, action) => {
                state.status = 'succeeded';
            })
            .addCase(createConversionRate.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(updateConversion.pending, state => {
                state.status = 'loading';
            })
            .addCase(updateConversion.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Don't try to update state.data for bulk updates - let the list refresh handle it
                // The update was successful, so we just mark it as succeeded
            })
            .addCase(updateConversion.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            });
        builder
            .addCase(deleteConversionRate.pending, state => {
                state.status = 'loading';
            })
            .addCase(deleteConversionRate.fulfilled, (state, action) => {
                state.status = 'succeeded';
                if (Array.isArray(state.data)) {
                    state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
                }
                if (Array.isArray(state.list)) {
                    state.list = state.list.filter(group => group.id !== action.payload); // Remove the deleted group from list
                }
            })
            .addCase(deleteConversionRate.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });
        builder
            .addCase(unArchiveConversionRate.pending, state => {
                state.status = 'loading';
            })
            .addCase(unArchiveConversionRate.fulfilled, (state, action) => {
                state.status = 'succeeded';
                if (Array.isArray(state.data)) {
                    state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
                }
            })
            .addCase(unArchiveConversionRate.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { resetConversionRateData, resetConversionRateList, resetCurrentConversionRatePage } = conversionRateSlice.actions;
export default conversionRateSlice.reducer;
